import React, { FC, useMemo, useCallback, useRef } from 'react';
import styles from './index.module.scss';
import Skeleton from 'src/common/Skeleton';
import Table from 'src/common/Table';
import { useTranslation } from 'react-i18next';
import {
  useLoadDocuments,
  useCurrentFolderContext,
  useUploadFile,
  useActiveDocNode
} from 'src/models/documentsLibrary/hooks';
import { ColumnProps } from 'antd/lib/table';
import { IDocumentsTableProps, DocumentsColumnsName, isUploadDndItem, IUploadDndItem } from '../types';
import Avatar from 'src/common/Avatar';
import RowComponent from '../DocumentsDnd/DragRow';
import dayjs from 'src/helpers/dayjs';
import DocumentsNodeName from './DocsName';
import { DocsNodeType, IFileNode } from 'src/models/documentsLibrary/types';
import { GetComponentProps } from 'rc-table/lib/interface';
import { DocumentsAttach, DocumentsDownload, DocumentsDelete, DocumentsRename } from '../DocumentsActions';
import DocumentsEmptyFolder from './EmptyFolder';
import classNames from 'classnames';
import { NativeTypes } from 'react-dnd-html5-backend';
import { useDrop, DropTargetMonitor } from 'react-dnd';
import DocumentsUploadView from './UploadView';
import { DELETED_USER } from 'src/models/users/types';

function useTableColumns(
  hideFields: readonly DocumentsColumnsName[],
  files: IFileNode[],
  onAttach?: (fileId: string) => void,
  isAttachLoading?: boolean
): ColumnProps<IDocumentsTableProps>[] {
  const { t } = useTranslation();
  return useMemo(() => {
    const temp: ColumnProps<IDocumentsTableProps>[] = [
      {
        title: t('Common.name'),
        key: 'name',
        dataIndex: 'name',
        className: styles.colName,
        ellipsis: true,
        render: (_, record) => <DocumentsNodeName docsNode={record} eventSource="Document Library" />
      },
      {
        title: t('Common.createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (_, record) => {
          const date = dayjs(record.createDateTime);
          return t('Common.Format.utcDateTimeFormat', { date: date.toDate() });
        },
        className: styles.colCreatedAt
      },
      {
        title: t('Common.createdBy'),
        key: 'createdBy',
        dataIndex: 'createdBy',
        className: styles.colCreatedBy,
        ellipsis: true,
        render: (_, record) => {
          return <Avatar user={record.createdBy ?? DELETED_USER} showTooltip={!!record.createdBy} size={'s'} />;
        }
      },
      {
        title: '',
        dataIndex: 'actions',
        key: 'actions',
        className: styles.colActions,
        onCell: () => ({
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
          }
        }),
        render: (_, record) => {
          const { nodeType, getUrl, name } = record;
          return (
            <ul className={styles.actions}>
              {nodeType === DocsNodeType.FILE && (
                <li className={styles.action}>
                  <DocumentsDownload getUrl={getUrl} name={name} />
                </li>
              )}
              <li className={styles.action}>
                <DocumentsRename docsNode={record} />
              </li>
              <li className={styles.action}>
                <DocumentsDelete docsNode={record} />
              </li>
            </ul>
          );
        }
      },
      {
        title: '',
        dataIndex: 'attach',
        key: 'attach',
        className: styles.colAttach,
        onCell: () => ({
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
          }
        }),
        render: (_, record) => {
          const { nodeType } = record;
          return (
            nodeType === DocsNodeType.FILE && (
              <DocumentsAttach file={record} files={files} onAttach={onAttach} isLoading={isAttachLoading} />
            )
          );
        }
      }
    ];

    return temp.filter(column => !hideFields.some(_ => column.dataIndex === _));
  }, [t, files, onAttach, hideFields, isAttachLoading]);
}

const components = {
  body: {
    row: RowComponent
  }
};

interface IProps {
  hideFields?: readonly DocumentsColumnsName[];
  files: IFileNode[];
  onAttach?: (fileId: string) => void;
  isAttachLoading?: boolean;
}

export const DocumentsView: FC<IProps> = props => {
  const { hideFields = [], onAttach, files, isAttachLoading } = props;
  const currentFolder = useCurrentFolderContext();
  const dropableRef = useRef<HTMLDivElement>(null);

  const { data: libraryDocs, loading } = useLoadDocuments(currentFolder.id);

  const [uploadFile] = useUploadFile();

  const [activeDocNode] = useActiveDocNode();

  const data = useMemo(
    () =>
      libraryDocs &&
      libraryDocs.map(
        (ls): IDocumentsTableProps => ({
          id: ls.id,
          name: ls.name,
          createDateTime: ls.createDateTime,
          createdBy: ls.createdBy,
          nodeType: ls.nodeType,
          putUrl: 'putUrl' in ls ? ls.putUrl : undefined,
          getUrl: 'getUrl' in ls ? ls.getUrl : undefined,
          childNodes: 'children' in ls ? ls.children : undefined,
          attachedTo: 'attachedTo' in ls ? ls.attachedTo : undefined,
          status: 'status' in ls ? ls.status : undefined
        })
      ),
    [libraryDocs]
  );

  const columns = useTableColumns(hideFields, files, onAttach, isAttachLoading);
  const onRow: GetComponentProps<IDocumentsTableProps> = useCallback(
    record => {
      const isUpdating = activeDocNode === record.id;
      return {
        className: classNames(styles.row, { [styles.isUpdating]: isUpdating }),
        record,
        onClick: e => {
          e.stopPropagation();
        }
      };
    },
    [activeDocNode]
  );

  const onDrop = useCallback(
    (item: IUploadDndItem, monitor: DropTargetMonitor) => {
      const didDrop = monitor.didDrop();
      if (didDrop) {
        return;
      }
      if (isUploadDndItem(item)) {
        const { files } = item;
        uploadFile(files[0], currentFolder.id);
      }
    },
    [currentFolder.id, uploadFile]
  );

  const [{ isOverCurrent, canDrop }, drop] = useDrop({
    accept: [NativeTypes.FILE],
    canDrop: () => {
      return !!libraryDocs && libraryDocs.length > 0;
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
      item: monitor.getItem(),
      getItemType: monitor.getItemType(),
      isOverCurrent: monitor.isOver({ shallow: true })
    }),
    drop: onDrop
  });

  drop(dropableRef);

  return loading ? (
    <Skeleton active={loading} />
  ) : (
    <div
      className={classNames(styles.docsContent, {
        [styles.isOverCurrent]: isOverCurrent
      })}
    >
      <div
        className={classNames(styles.docsTable, {
          [styles.isDropable]: canDrop
        })}
        ref={dropableRef}
      >
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          locale={{
            emptyText: <DocumentsEmptyFolder />
          }}
          rowKey={'id'}
          className={classNames(styles.table, {
            [styles.emptyTable]: data?.length === 0
          })}
          components={components}
          onRow={onRow}
        />
      </div>
      {isOverCurrent && <DocumentsUploadView name={currentFolder.name} />}
    </div>
  );
};

export default DocumentsView;
